import React, { useCallback, useContext } from 'react'
import { barBox } from '../lib/geometry'
import type { DateRange } from '../lib/geometry'
import { useGanttStoreApi } from '../store/ganttStore'
import type { ScheduleActivity } from '../types'
import { GanttHoverDispatchContext, useHoveredActivityId } from './gantt-hover'
import BaselineBar from './timeline/BaselineBar'
import ConstraintPin from './timeline/ConstraintPin'
import MilestoneMarker from './timeline/MilestoneMarker'
import SummaryBar from './timeline/SummaryBar'
import TaskBar from './timeline/TaskBar'

interface Props {
  activity: ScheduleActivity
  top: number
  range: DateRange
  pxPerDay: number
  selected: boolean
  showBaseline: boolean
  showCriticalPath: boolean
  canEdit: boolean
  /** Visible row ids top→bottom, for the click handler's shift-range selection. */
  visibleOrder: string[]
}

/**
 * One row's bar/milestone/summary, its optional baseline overlay and constraint pin (spec B
 * §8.4) — extracted out of `GanttTimeline`'s render loop (Task 18) so it can call
 * `useHoveredActivityId` itself: a per-row component is the only way to get a truly per-row
 * "is this bar hovered" re-render (see gantt-hover.ts) — a hook call inside a `.map()` callback
 * isn't legal, and reading the hover value directly in `GanttTimeline` would reintroduce the
 * whole-timeline re-render churn the Task 10 review eliminated. Only Task/MilestoneMarker get
 * resize/link handles (`showHandles`); summaries don't participate in either drag.
 */
const ActivityBar: React.FC<Props> = ({ activity, top, range, pxPerDay, selected, showBaseline, showCriticalPath, canEdit, visibleOrder }) => {
  const api = useGanttStoreApi()
  const setHover = useContext(GanttHoverDispatchContext)
  const hovered = useHoveredActivityId(activity.id)
  const showHandles = canEdit && (hovered || selected)
  const box = barBox(activity, range.start, pxPerDay)

  const onPointerEnter = useCallback((e: React.PointerEvent) => setHover({ kind: 'activity', id: activity.id, x: e.clientX, y: e.clientY }), [setHover, activity.id])
  const onPointerMove = useCallback((e: React.PointerEvent) => setHover({ kind: 'activity', id: activity.id, x: e.clientX, y: e.clientY }), [setHover, activity.id])
  const onPointerLeave = useCallback(() => setHover(null), [setHover])
  const onClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    api.getState().select(activity.id, e.shiftKey ? 'range' : (e.ctrlKey || e.metaKey) ? 'toggle' : 'replace', visibleOrder)
  }, [api, activity.id, visibleOrder])

  const hoverProps = { onPointerEnter, onPointerMove, onPointerLeave, onClick }

  return (
    <g data-activity-id={activity.id}>
      {showBaseline && activity.baselineStart && activity.baselineFinish && (
        <BaselineBar
          {...barBox({ start: activity.baselineStart, finish: activity.baselineFinish, isMilestone: activity.isMilestone }, range.start, pxPerDay)}
          rowTop={top}
          isMilestone={activity.isMilestone}
          isSummary={activity.isSummary}
        />
      )}
      {activity.isSummary ? (
        <SummaryBar x={box.x} width={box.width} rowTop={top} label={activity.name} percent={activity.rolledPercent} selected={selected} shiftUp={showBaseline} {...hoverProps} />
      ) : activity.isMilestone ? (
        <MilestoneMarker
          x={box.x} rowTop={top} label={activity.name}
          critical={showCriticalPath && activity.isCritical} completed={!!activity.actualFinish} selected={selected} shiftUp={showBaseline}
          activityId={activity.id} showHandles={showHandles}
          {...hoverProps}
        />
      ) : (
        <TaskBar
          x={box.x} width={box.width} rowTop={top} label={activity.name}
          percent={activity.actualFinish ? 100 : activity.percentComplete}
          critical={showCriticalPath && activity.isCritical} selected={selected} shiftUp={showBaseline}
          activityId={activity.id} showHandles={showHandles}
          {...hoverProps}
        />
      )}
      {activity.constraintType !== 'ASAP' && <ConstraintPin x={box.x} rowTop={top} />}
    </g>
  )
}

export default React.memo(ActivityBar)
